import { RequestTypeIds } from '@src/utils/enums';
import { getLatestFormVersionByRequestType } from '@pages/Forms/registry/latestVersions';
import { QuestionDataById } from '@components/dynamic-form/types';

import { nwpQuestionsById as nwpQuestionsByIdV1 } from '@pages/Forms/input-forms/NonPcnForm/nonPcnData.nwpQuestions';
import { generalQuestionsById as generalQuestionsByIdV1 } from '@pages/Forms/input-forms/NonPcnForm/nonPcnData.generalConditionQuestions';
import { sectionsById as sectionsByIdV1 } from '@pages/Forms/input-forms/NonPcnForm/nonPcnData.sections';
import {
  getParentObject,
  nonPcnQuestionNameById,
  nonPcnQuestionIdByName,
} from '@pages/Forms/input-forms/NonPcnForm/nonPcnData.types';

import { nwpQuestionsById as nwpQuestionsByIdV2 } from '@pages/Forms/input-forms/NonPcnForm/config/questions/nonPcnData.nwpQuestions.V2';
import { generalQuestionsById as generalQuestionsByIdV2 } from '@pages/Forms/input-forms/NonPcnForm/config/questions/nonPcnData.generalConditionQuestions.V2';
import { sectionsById as sectionsByIdV2 } from '@pages/Forms/input-forms/NonPcnForm/config/questions/nonPcnData.sections.V2';
import {
  getParentObjectV2,
  nonPcnQuestionNameByIdV2,
  nonPcnQuestionIdByNameV2,
} from '@pages/Forms/input-forms/NonPcnForm/nonPcnData.types.V2';

export type NonPcnVariant = 'nonPcn';

type SectionsById = typeof sectionsByIdV1 | typeof sectionsByIdV2;

export type NonPcnConfig = {
  questionsById: QuestionDataById;
  sectionsById: SectionsById;
  nonPcnQuestionNameById: typeof nonPcnQuestionNameById | typeof nonPcnQuestionNameByIdV2;
  nonPcnQuestionIdByName: typeof nonPcnQuestionIdByName | typeof nonPcnQuestionIdByNameV2;
  getParentObject: typeof getParentObject | typeof getParentObjectV2;
};

type NonPcnConfigKey = `${NonPcnVariant}@${number}`;

const questionsByIdV1: QuestionDataById = {
  ...nwpQuestionsByIdV1,
  ...generalQuestionsByIdV1,
};

const questionsByIdV2: QuestionDataById = {
  ...nwpQuestionsByIdV2,
  ...generalQuestionsByIdV2,
};

export const nonPcnConfigByKey: Record<NonPcnConfigKey, NonPcnConfig> = {
  'nonPcn@1': {
    questionsById: questionsByIdV1,
    sectionsById: sectionsByIdV1,
    nonPcnQuestionNameById,
    nonPcnQuestionIdByName,
    getParentObject,
  },

  'nonPcn@2': {
    questionsById: questionsByIdV2,
    sectionsById: sectionsByIdV2,
    nonPcnQuestionNameById: nonPcnQuestionNameByIdV2,
    nonPcnQuestionIdByName: nonPcnQuestionIdByNameV2,
    getParentObject: getParentObjectV2,
  },
};

export const resolveNonPcnConfig = (variant: NonPcnVariant, version: number): NonPcnConfig => {
  const configsForVariant = (Object.entries(nonPcnConfigByKey) as Array<[NonPcnConfigKey, NonPcnConfig]>)
    .filter(([key]) => key.startsWith(variant + '@'))
    .map(([key, config]) => ({
      version: Number(key.split('@')[1]),
      config,
    }))
    .sort((a, b) => a.version - b.version);

  if (configsForVariant.length === 0) {
    console.error('No nonPCN configs found for variant="' + variant + '". Check nonPcnConfig keys.');
  }

  return (
    configsForVariant.find((c) => c.version === version)?.config ??
    [...configsForVariant].reverse().find((c) => c.version <= version)?.config ??
    configsForVariant[configsForVariant.length - 1].config
  );
};

export const resolveNonPcnConfigForRequest = (args: {
  requestType?: string | undefined;
  formVersion: number | null | undefined;
}): NonPcnConfig => {
  const variant: NonPcnVariant = 'nonPcn';
  const versionToUse =
    args.formVersion ?? getLatestFormVersionByRequestType(RequestTypeIds.NonPreconstructionNotification);

  return resolveNonPcnConfig(variant, versionToUse!);
};
